import getMenuItems from 'util/get-menu-items';

import type React from 'react';
import { useCallback } from 'react';

import { CalenderIcon, ChatIcon } from 'assets/svg-icons';
import { useCall } from 'context/call-context';
import { checkColorBrightness, getContrastingColor } from 'helper/check-color-brightness';
import { isNullOrEmpty } from 'helper/is-null-or-empty';
import { renderPrimaryIconBaseOnBaseUrl } from 'helper/popup-chat/popup-chat-helper';
import { Action, Index } from 'hooks/models';
import useActivityLogger from 'hooks/useActivityLogger';
import { t } from 'i18next';
import type { MenuOption, OtherWidget, Theme, WidgetOption } from 'types';
import { MenuType } from 'types';

import classes from './chat-bubble-strip.module.scss';

interface IExpandBody {
  isChatEnabled: boolean;
  onlineMenu: MenuOption[];
  offlineMenu: MenuOption[];
  onlineButton: boolean;
  offlineButton: boolean;
  otherWidgets: OtherWidget[];
  otherWidgetsRef: React.MutableRefObject<Map<string, boolean>>;
  schedulingOption: { enabled: boolean };
  theme: Theme;
  widgetOption: WidgetOption;
  onBubbleButtonClick: () => void;
  onAppointmentClick: () => void;
  onClickAvatar: () => void;
  onPressOfflineLink: () => void;
  setOnlineButton: (value: boolean) => void;
  setOfflineButton: (value: boolean) => void;
  toggleOtherWidget: (value: OtherWidget) => void;
}

const ExpandStripBody = ({
  isChatEnabled,
  onlineMenu,
  offlineMenu,
  onlineButton,
  offlineButton,
  otherWidgets,
  otherWidgetsRef,
  schedulingOption,
  theme,
  widgetOption,
  onBubbleButtonClick,
  onPressOfflineLink,
  onClickAvatar,
  onAppointmentClick,
  setOnlineButton,
  setOfflineButton,
  toggleOtherWidget
}: IExpandBody) => {
  const { avatarImg, texts, color } = widgetOption;
  const textColor = getContrastingColor(theme?.textColor || '#ffffff');
  const primaryTextColor = checkColorBrightness({
    backgroundColor: color || '#ffffff',
    textColor: theme.textColor ?? '#000000'
  });
  const { productType } = useCall();
  const { log } = useActivityLogger();

  const renderPrimaryExpandButtons = () => {
    if (isChatEnabled) {
      return (
        <button
          id="conferwith-start-video-call"
          style={{ background: color, color: primaryTextColor }}
          onClick={onBubbleButtonClick}
          aria-label={`Video button`}
        >
          {renderPrimaryIconBaseOnBaseUrl({ primaryTextColor })}
          {/* Text: Live video */}
          <span>{texts?.startCallText ? texts?.startCallText : t('chatBubbleV2.primaryVideoCallText')}</span>
        </button>
      );
    }

    return (
      schedulingOption.enabled && (
        <button
          aria-label={`Book button`}
          style={{
            background: '#c5c5c5a3',
            color: textColor,
            fontFamily: theme?.font
          }}
          onClick={onAppointmentClick}
        >
          <CalenderIcon color={textColor} />
          <span className={classes.cwBtnText}>
            {/* Text: Book a Call */}
            {texts?.bookAppointmentText ? texts?.bookAppointmentText : t('chatBubbleV2.primaryBookingCallText')}
          </span>
        </button>
      )
    );
  };

  const renderMenuButton = (data: MenuOption, isOnline: boolean) => {
    const { externalFunction, link, label } = getMenuItems(data, isOnline ? onlineButton : offlineButton);
    if (!isNullOrEmpty(productType) && !data.enabled) {
      return;
    }
    const buttonAction = () => {
      void log({
        action: Action.ON_SECONDARY_BUTTON_CLICKED,
        description: `customer clicked secondary button`,
        index: Index.ACTIVITY,
        label: 'CUSTOMER_WIDGET'
      });

      if (link) {
        window.location.href = link || '';
        return;
      }
      if (externalFunction) {
        externalFunction();
      }
      if (data.data.type !== MenuType.JSON) return;
      if (isOnline) {
        setOnlineButton(!onlineButton);
        return;
      }
      onPressOfflineLink();
      setOfflineButton(!offlineButton);
    };

    return (
      <button
        aria-label={`${data.data.icon} button`}
        key={data.data.icon}
        onClick={buttonAction}
      >
        <i
          className={`${data.data.icon} ${isOnline ? '' : classes.cwButtonIcons}`}
          style={{ fontSize: '12px' }}
        />
        <span
          className={classes.cwBtnText}
          style={{ fontFamily: theme?.font }}
        >
          {label}
        </span>
      </button>
    );
  };

  const renderSecondaryExpandButtons = () => {
    if (isChatEnabled) {
      return onlineMenu.map((data) => renderMenuButton(data, true));
    }
    return offlineMenu.map((data) => renderMenuButton(data, false));
  };

  const renderOtherWidget = useCallback(
    () =>
      otherWidgets &&
      otherWidgets
        .filter(
          (otherWidget) =>
            otherWidget?.label &&
            ((otherWidget.id && otherWidgetsRef.current.get(otherWidget.id) !== undefined) ||
              (otherWidget.function && otherWidgetsRef.current.get(otherWidget.function) !== undefined))
        )
        .map((otherWidget) => (
          <button
            aria-label={`Chat button`}
            key={otherWidget.id}
            onClick={() => toggleOtherWidget(otherWidget)}
            style={{
              border: `2px solid ${color}`,
              color: textColor,
              display: otherWidget?.label?.trim() === '' || !otherWidget?.label ? 'none' : 'flex',
              fontFamily: theme?.font
            }}
            className={classes.cwSecondaryBtn}
          >
            <ChatIcon color={textColor} />
            <span className={classes.cwBtnText}>{otherWidget.label}</span>
          </button>
        )),
    [otherWidgetsRef, otherWidgets]
  );

  return (
    <div className={classes.cwExpandBody}>
      <div className={classes.cwCirWithPointercle}>
        <div
          role="button"
          onClick={onClickAvatar}
          aria-label={`image call button`}
          className={classes.circle}
          style={!isChatEnabled ? { border: '3px solid #888888' } : {}}
        >
          <img
            src={avatarImg}
            alt="customer avatar"
          />
        </div>
        <span
          className={classes.cwOnlineStatus}
          style={!isChatEnabled ? { background: '#888888' } : {}}
        >
          {/* Text: Live/Offline */}
          {isChatEnabled ? t('chatBubbleV2.live') : t('chatBubbleV2.offline')}
        </span>
      </div>
      <div className={classes.cwExpandBodyButtonsWrapper}>
        <div className={classes.cwWidgetcontent}>
          <p>
            {isChatEnabled
              ? texts?.welcomeText || (
                  <span>
                    {/* Text: Need some help? */}
                    {t('chatBubbleV2.welcomePlaceholder1')}
                    {/* Text: Our brand ambassadors can help you shop over a video call */}
                    <b>{t('chatBubbleV2.welcomePlaceholder2')}</b>
                  </span>
                )
              : texts.offlineWelcomeText}
          </p>
        </div>
        <div className={classes.cwPrimaryBtn}>{renderPrimaryExpandButtons()}</div>
        <div className={classes.cwSecondaryBtn}>
          {/* Online and Offline Menu Buttons */}
          {renderSecondaryExpandButtons()}
          {/* OtherWidget Menu Buttons */}
          {renderOtherWidget()}
        </div>
      </div>
    </div>
  );
};

export default ExpandStripBody;
